import { useTranslation } from 'react-i18next';
import { GripVertical, Plus, Trash2 } from 'lucide-react';

import { Button, Checkbox, IconButton } from '@/components';

import type { BoxShadowItem } from '../styleTypes';

import { ColorPickerAdvanced } from './ColorPickerAdvanced';
import { NumberInput } from './NumberInput';

interface ShadowEditorProps {
  shadows: BoxShadowItem[] | undefined;
  onChange: (shadows: BoxShadowItem[] | undefined) => void;
}

const DEFAULT_SHADOW: BoxShadowItem = {
  x: 0,
  y: 4,
  blur: 6,
  spread: -1,
  color: 'rgba(0,0,0,0.1)',
  inset: false,
};

export function ShadowEditor({ shadows, onChange }: ShadowEditorProps) {
  const { t } = useTranslation();
  const items = shadows || [];

  const addShadow = () => {
    onChange([...items, { ...DEFAULT_SHADOW }]);
  };

  const removeShadow = (index: number) => {
    const next = items.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : undefined);
  };

  const updateShadow = (index: number, updates: Partial<BoxShadowItem>) => {
    const current = items[index];
    if (!current) return;
    const next = [...items];
    next[index] = { ...current, ...updates };
    onChange(next);
  };

  const previewShadow = () => {
    if (items.length === 0) return 'none';
    return items
      .map(
        (s) => `${s.inset ? 'inset ' : ''}${s.x}px ${s.y}px ${s.blur}px ${s.spread}px ${s.color}`,
      )
      .join(', ');
  };

  return (
    <div className="space-y-3">
      {/* Preview */}
      {items.length > 0 && (
        <div className="flex justify-center py-3">
          <div
            className="w-20 h-14 bg-white dark:bg-gray-700 rounded-lg"
            style={{ boxShadow: previewShadow() }}
          />
        </div>
      )}

      {/* Shadow list */}
      {items.map((shadow, i) => (
        <div key={i} className="p-2.5 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <GripVertical className="w-3 h-3" />
              {t('tenant.systemModuleBuilder.style.shadow.item', { index: i + 1 })}
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={shadow.inset}
                onChange={(e) => updateShadow(i, { inset: e.target.checked })}
                label={
                  <span className="text-[10px] text-gray-400">
                    {t('tenant.systemModuleBuilder.style.shadow.inset')}
                  </span>
                }
                className="h-3 w-3"
              />
              <IconButton
                variant="ghost"
                size="sm"
                aria-label={t('tenant.systemModuleBuilder.style.shadow.remove')}
                onClick={() => removeShadow(i)}
                className="text-gray-400 hover:text-red-500"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </IconButton>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <NumberInput
              value={shadow.x}
              onChange={(v) => updateShadow(i, { x: v ?? 0 })}
              min={-50}
              max={50}
              label="X"
              unit="px"
              className="min-w-[7rem] flex-1"
            />
            <NumberInput
              value={shadow.y}
              onChange={(v) => updateShadow(i, { y: v ?? 0 })}
              min={-50}
              max={50}
              label="Y"
              unit="px"
              className="min-w-[7rem] flex-1"
            />
            <NumberInput
              value={shadow.blur}
              onChange={(v) => updateShadow(i, { blur: v ?? 0 })}
              min={0}
              max={100}
              label={t('tenant.systemModuleBuilder.style.shadow.blur')}
              unit="px"
              className="min-w-[7rem] flex-1"
            />
            <NumberInput
              value={shadow.spread}
              onChange={(v) => updateShadow(i, { spread: v ?? 0 })}
              min={-50}
              max={50}
              label={t('tenant.systemModuleBuilder.style.shadow.spread')}
              unit="px"
              className="min-w-[7rem] flex-1"
            />
          </div>
          <ColorPickerAdvanced
            color={shadow.color}
            onChange={(c) => updateShadow(i, { color: c || 'rgba(0,0,0,0.1)' })}
            label={t('tenant.systemModuleBuilder.style.common.color')}
            allowClear={false}
          />
        </div>
      ))}

      <Button
        type="button"
        variant="ghost"
        onClick={addShadow}
        leftIcon={<Plus className="w-3.5 h-3.5" />}
        className="w-full rounded-lg border border-dashed border-gray-300 py-1.5 text-xs font-normal text-gray-500 hover:border-accent-400 hover:text-accent-500 dark:border-gray-600"
      >
        {t('tenant.systemModuleBuilder.style.shadow.add')}
      </Button>
    </div>
  );
}
